import { CreditApplicationAttributes } from "@albatrosdeveloper/ave-models-package";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums/status.enum";
import { CommonModule, DecimalPipe } from "@angular/common";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from "@angular/material/menu";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSidenavContainer } from "@angular/material/sidenav";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { SmartGridComponent, ColDef, GridConfig, PageChangedEvent, SortChangedEvent } from 'app/shared/components/smart-grid';
import { ActivatedRoute, Router } from "@angular/router";
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { FilterSectionComponent } from "app/common/components/filter-section/filter-section.component";
import { FilterComponent } from "app/common/components/filter/filter.component";
import { SearchService } from "app/layout/common/search-page/search-page.service";

import { CustomMatPaginatorIntl } from "app/mock-api/common/customPaginatorIntl";
import { BusinessPartnersFiltersComponent } from "app/modules/components/filters/business-partners-filters/business-partners-filters.component";
import { DatesFiltersComponent } from "app/modules/components/filters/dates-filters/dates-filters.component";
import { DecimalFormatPipe } from "app/pipes/decimal-company.pipe";
import { FormatOtherDateHourPipe } from "app/pipes/other-date.pipe";
import { DateHelper } from "app/utils/date.helper";
import { Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, skip, take } from "rxjs/operators";

import { DocumentsFiltersComponent } from "../../filters/documents-filters/documents-filters.component";
import { StatusFiltersComponent } from "../../filters/status-filters/status-filters.component";
import { CreditRequestService } from "./credit-application.component.service";
import { CreditRequestFilters } from "./interfaces/credit-request.model";
import { CreditApplicationReportExport } from "../../assistant/delivery/loads/exports/reports/credit-application.export";
import { CREDIT_APPLICATION_EXPORT_ID, CREDIT_APPLICATION_PROPERTIES, CREDIT_APPLICATION_TITLE } from "../../assistant/delivery/loads/exports/reports/credit-application.constants";
import { ExportDialogComponent, ExportOptions } from "app/layout/export-overlay";
import { IPagination } from "app/common/interfaces";
import { CompanyCurrencyPipe } from "app/pipes/currency.pipe";
import { DecimalConfigService } from "app/services/decimal-config.service";

@Component({
  selector: "app-credit-application",
  standalone: true,
  imports: [
    CommonModule,
    HoverMenuDirective,
    MatSidenavContainer,
    MatButtonModule,
    MatIconModule,
    SmartGridComponent,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    FilterComponent,
    FilterSectionComponent,
    DatesFiltersComponent,
    BusinessPartnersFiltersComponent,
    DocumentsFiltersComponent,
    DecimalFormatPipe,
    FormatOtherDateHourPipe,
    StatusFiltersComponent,
    CompanyCurrencyPipe,
  ],
  providers: [
    DecimalPipe,
    CreditApplicationReportExport,
  ],
  templateUrl: "./credit-application.component.html",
  styleUrl: "./credit-application.component.scss",
})
export class CreditApplicationComponent implements OnInit, OnDestroy {
  private decimalPipe = new DecimalPipe('en-US');

  // SmartGrid Data Structures
  data: CreditApplicationAttributes[] = [];
  columnDefs: ColDef<CreditApplicationAttributes>[] = [];
  gridConfig: GridConfig<CreditApplicationAttributes> = {
    rowIdField: 'id' as any,
    dataMode: 'api',
    sorting: {
      enabled: true,
      mode: 'api',
      multiColumn: false,
      defaultSort: [
        { field: 'creationDateTime' as any, direction: 'desc' }
      ],
    },
    pagination: {
      enabled: true,
      mode: 'api',
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
      showFirstLastButtons: true,
    },
    search: {
      enabled: true,
      mode: 'api',
      showSearchInput: false,
    },
    columnFilters: {
      enabled: true,
      mode: 'local',
    },
    selection: {
      enabled: false,
    },
    styling: {
      maxHeight: '85vh',
      minHeight: '400px',
      fontSize: '12px',
      cellPaddingX: '3px',
      cellPaddingY: '0px',
      density: 'comfortable',
    },
  };

  totalItems: number = 0;
  pageSize: number = 50;
  pageIndex: number = 1;
  sortActive: string = "creationDateTime";
  sortDirection: "asc" | "desc" = "desc";
  search: string = "";
  searchSubscription: Subscription;
  form: FormGroup;
  loadBusinessPartnersData = false;
  selectedStatuses: number[] = [];
  loadDocumentsData = false;
  selectedCustomerType: string[] = [];

  private isInitialized = false;
  private isLoadingRequest = false;
  private requestIdsToFilter: number[] = [];
  private subs: Subscription[] = [];

  isLoading: boolean = false;
  hasError: boolean = false;
  errorMessage: string = "";

  constructor(
    private readonly creditRequestService: CreditRequestService,
    private readonly searchService: SearchService,
    private readonly snackBar: MatSnackBar,
    private readonly matDialog: MatDialog,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly activatedRoute: ActivatedRoute,
    private creditApplicationReportExport: CreditApplicationReportExport,
    private readonly companyCurrencyPipe: CompanyCurrencyPipe,
    private readonly decimalConfigService: DecimalConfigService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.subs.push(
      this.activatedRoute.queryParams.pipe(take(1)).subscribe((params) => {
        if (params["requestIds"]) {
          this.requestIdsToFilter = params["requestIds"]
            .split(",")
            .map((id: string) => Number(id));
        }
      })
    );

    this.initializeColumnDefs();
    this.onHandleSearch();
    this.getList();
    this.isInitialized = true;
  }

  private initializeColumnDefs(): void {
    this.columnDefs = [
      // 1. ID Solicitud
      {
        field: 'id',
        headerName: '# Solicitud',
        minWidth: 120,
        sortable: true,
        valueFormatter: (value, data) => {
          return String(value || '-');
        },
        cellClass: 'font-semibold',
        filterOptions: {
          valueExtractor: (item: any) => String(item.id || '-'),
          customFilterFn: (rowData: any, filterValue: string) => {
            const value = String(rowData.id || '-');
            if (!value || value === '-') return !filterValue;
            return value === filterValue;
          },
        },
      },
      // 2. Usuario (TODO: Crear cell renderer custom)
      {
        field: 'user' as any,
        headerName: 'Usuario',
        minWidth: 250,
        sortable: true,
        valueFormatter: (value, data) => {
          const user = data.user;
          return user ? `${user.username || '-'}\n${user.first_name || ''} ${user.last_name || ''}` : '-';
        },
        filterOptions: {
          valueExtractor: (item: any) => {
            const user = item.user;
            return user ? `${user.username || '-'} ${user.first_name || ''} ${user.last_name || ''}` : '-';
          },
          customFilterFn: (rowData: any, filterValue: string) => {
            const user = rowData.user;
            const displayValue = user ? `${user.username || '-'} ${user.first_name || ''} ${user.last_name || ''}` : '-';
            if (!displayValue || displayValue === '-') return !filterValue;
            return displayValue === filterValue;
          },
        },
      },
      // 3. Cliente (TODO: Crear cell renderer custom)
      {
        field: 'businessPartner' as any,
        headerName: 'Cliente',
        minWidth: 200,
        sortable: true,
        valueFormatter: (value, data) => {
          const bp = data.businessPartner;
          return bp ? `${bp.externalKeys?.code || '-'}\n${bp.name || '-'}` : 'SIN CLIENTE ASOCIADO';
        },
        filterOptions: {
          valueExtractor: (item: any) => {
            const bp = item.businessPartner;
            return bp ? `${bp.externalKeys?.code || '-'} ${bp.name || '-'}` : 'SIN CLIENTE ASOCIADO';
          },
          customFilterFn: (rowData: any, filterValue: string) => {
            const bp = rowData.businessPartner;
            const displayValue = bp ? `${bp.externalKeys?.code || '-'} ${bp.name || '-'}` : 'SIN CLIENTE ASOCIADO';
            if (!displayValue) return !filterValue;
            return displayValue === filterValue;
          },
        },
      },
      // 4. Fecha Creación (TODO: Crear cell renderer con hora)
      {
        field: 'creationDateTime' as any,
        headerName: 'Fecha Creación',
        minWidth: 180,
        sortable: true,
        valueFormatter: (value, data) => {
          if (!value) return '-';
          const d = new Date(value);
          return `${d.toLocaleDateString()}\n${d.toLocaleTimeString()}`;
        },
        filterOptions: {
          valueExtractor: (item: any) => {
            if (!item.creationDateTime) return '-';
            const date = new Date(item.creationDateTime);
            return date.toLocaleDateString('es-ES');
          },
          customFilterFn: (rowData: any, filterValue: string) => {
            if (!rowData.creationDateTime) return !filterValue;
            const date = new Date(rowData.creationDateTime);
            const formattedDate = date.toLocaleDateString('es-ES');
            return formattedDate === filterValue;
          },
        },
      },
      // 5. Límite Solicitado
      {
        field: 'amountCreditRequested',
        headerName: 'Límite Solicitado',
        minWidth: 180,
        sortable: true,
        valueFormatter: (value, data) => {
          return value != null ? this.formatCurrency(value) : '-';
        },
        cellClass: 'font-semibold',
        filterOptions: this.createCurrencyFilterOptions('amountCreditRequested'),
      },
      // 6. Límite Aprobado
      {
        field: 'amountCreditApproved',
        headerName: 'Límite Aprobado',
        minWidth: 180,
        sortable: true,
        valueFormatter: (value, data) => {
          return value != null ? this.formatCurrency(value) : '-';
        },
        cellClass: 'font-semibold',
        filterOptions: this.createCurrencyFilterOptions('amountCreditApproved'),
      },
      // 7. Fecha Aprobación
      {
        field: 'approvalDate',
        headerName: 'Fecha Aprobación',
        minWidth: 180,
        sortable: true,
        valueFormatter: (value, data) => {
          if (!value) return '-';
          const d = new Date(value);
          return `${d.toLocaleDateString()}\n${d.toLocaleTimeString()}`;
        },
        filterOptions: {
          valueExtractor: (item: any) => {
            if (!item.approvalDate) return '-';
            const date = new Date(item.approvalDate);
            return date.toLocaleDateString('es-ES');
          },
          customFilterFn: (rowData: any, filterValue: string) => {
            if (!rowData.approvalDate) return !filterValue;
            const date = new Date(rowData.approvalDate);
            const formattedDate = date.toLocaleDateString('es-ES');
            return formattedDate === filterValue;
          },
        },
      },
      // 8. Usuario Aprobador
      {
        field: 'approvalUser' as any,
        headerName: 'Usuario Aprobador',
        minWidth: 200,
        sortable: true,
        valueFormatter: (value, data) => {
          const user = data.approvalUser;
          return user ? `${user.username || '-'}\n${user.first_name || ''} ${user.last_name || ''}` : '-';
        },
        filterOptions: {
          valueExtractor: (item: any) => {
            const user = item.approvalUser;
            return user ? `${user.username || '-'} ${user.first_name || ''} ${user.last_name || ''}` : '-';
          },
          customFilterFn: (rowData: any, filterValue: string) => {
            const user = rowData.approvalUser;
            const displayValue = user ? `${user.username || '-'} ${user.first_name || ''} ${user.last_name || ''}` : '-';
            if (!displayValue || displayValue === '-') return !filterValue;
            return displayValue === filterValue;
          },
        },
      },
      // 9. Motivo Rechazo
      {
        field: 'reasonRejection' as any,
        headerName: 'Motivo Rechazo',
        minWidth: 200,
        sortable: true,
        valueFormatter: (value, data) => {
          return data.reasonRejection?.name || '-';
        },
        filterOptions: {
          valueExtractor: (item: any) => item.reasonRejection?.name || '-',
          customFilterFn: (rowData: any, filterValue: string) => {
            const value = rowData.reasonRejection?.name || '-';
            if (!value || value === '-') return !filterValue;
            return value === filterValue;
          },
        },
      },
      // 10. Estado
      {
        field: 'statusId',
        headerName: 'Estado',
        minWidth: 150,
        sortable: true,
        valueFormatter: (value, data) => {
          return this.getStatusText(data.statusId).toUpperCase();
        },
        filterOptions: {
          valueExtractor: (item: any) => this.getStatusText(item.statusId).toUpperCase(),
          customFilterFn: (rowData: any, filterValue: string) => {
            const value = this.getStatusText(rowData.statusId).toUpperCase();
            if (!value) return !filterValue;
            return value === filterValue;
          },
        },
      },
    ];
  }

  private formatDecimal(value: number): string {
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  getStatusIcon(status: number): string {
    const icons: Record<number, string> = {
      1: "heroicons_solid:check-circle",
      0: "heroicons_solid:x-circle",
      6: "heroicons_solid:play-circle",
      7: "heroicons_solid:check-circle",
      14: "heroicons_solid:check-circle",
      8: "heroicons_solid:x-circle",
    };
    return icons[status] || "heroicons_solid:question-mark-circle";
  }

  getStatusText(status: number): string {
    const texts: Record<number, string> = {
      [StatusEnum.ACTIVE]: "Activo", // 1
      [StatusEnum.INACTIVE]: "Anulado", // 0
      [StatusEnum.ORDER_IN_PROGRESS]: "En progreso", // 6
      [StatusEnum.ORDER_APPROVED]: "Aprobado", // 7
      [StatusEnum.ORDER_PARTIAL_APPROVED]: "Aprobado Parcialmente", // 14
      [StatusEnum.ORDER_REJECTED]: "Rechazado", // 8
    };
    return texts[status] || "Desconocido";
  }

  onDocumentsExpanded(): void {
    this.loadDocumentsData = true;
  }

  private initializeForm(): void {
    const today = new Date();
    this.form = this.fb.group({
      start: [today],
      end: [today],
      creation_start: [null],
      creation_end: [null],
      approval_start: [null],
      approval_end: [null],
      assignedUserId: [[]],
      userId: [[]],
      payingBusinessPartnerId: [[]],
      requestTypeId: [[]],
      searchAssignedUser: [],
    });
  }

  private onHandleSearch(): void {
    this.searchSubscription = this.searchService.searchTerm$
      .pipe(skip(1), debounceTime(300), distinctUntilChanged())
      .subscribe((term) => {
        if (this.isInitialized) {
          this.pageIndex = 1;
          this.search = term;
          this.onFilter();
        }
      });
  }

  private getSortField(): keyof CreditApplicationAttributes {
    const sortFieldMapping: Record<string, keyof CreditApplicationAttributes> =
      {
        id: "id",
        businessPartner: <any>"businessPartner.name",
        user: <any>"user.username",
        creationDateTime: <any>"creationDateTime",
        approvalDate: "approvalDate",
        approvalUser: <any>"approvalUser.username",
        amountCreditRequested: "amountCreditRequested",
        amountCreditApproved: "amountCreditApproved",
        statusId: "statusId",
        statusERP: "statusERP",
      };
    return sortFieldMapping[this.sortActive] || <any>"creationDateTime";
  }

  private buildFilters(): CreditRequestFilters {
    const formValue = this.form.value;
    const hasRequestIds =
      this.requestIdsToFilter && this.requestIdsToFilter.length > 0;
    const hasSearch = this.search && this.search.trim().length > 0;

    let isExtension: boolean | null = null;
    if (formValue.requestTypeId && formValue.requestTypeId.length > 0) {
      if (formValue.requestTypeId.length === 1) {
        isExtension = formValue.requestTypeId[0];
      }
    }
    return {
      //Fechas bloqueadas si hasRequestIds Y no hay search
      createdDateFrom:
        hasRequestIds && !hasSearch
          ? null
          : formValue.creation_start
            ? DateHelper.getStartOfDay(formValue.creation_start)
            : formValue.start
              ? DateHelper.getStartOfDay(formValue.start)
              : null,
      createdDateTo:
        hasRequestIds && !hasSearch
          ? null
          : formValue.creation_end
            ? DateHelper.getEndOfDay(formValue.creation_end)
            : formValue.end
              ? DateHelper.getEndOfDay(formValue.end)
              : formValue.start
                ? DateHelper.getEndOfDay(formValue.start)
                : null,
      approvalDateFrom:
        hasRequestIds && !hasSearch
          ? null
          : formValue.approval_start
            ? DateHelper.getStartOfDay(formValue.approval_start)
            : null,
      approvalDateTo:
        hasRequestIds && !hasSearch
          ? null
          : formValue.approval_end
            ? DateHelper.getEndOfDay(formValue.approval_end)
            : null,
      statusId:
        hasRequestIds && !hasSearch
          ? []
          : this.selectedStatuses?.length > 0
            ? this.selectedStatuses
            : [],
      isExtension: isExtension,
      businessPartnerId: formValue.payingBusinessPartnerId || [],
      userId: this.form.get("assignedUserId")?.value || [], // Asesor comercial
      assignedUserId: [],
      approvalUserId: [],
      companyId: [],
      subRouteId: [],
      customerType: this.selectedCustomerType || [],
      requestIds:
        hasRequestIds && !hasSearch ? this.requestIdsToFilter : undefined,
    };
  }

  onExportButtonPressed() {
    const sortDirection: "ASC" | "DESC" =
      this.sortDirection === "asc" ? "ASC" : "DESC";

    const optionsFilters: IPagination<CreditApplicationAttributes> = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.search,
      sort: this.getSortField(),
      sortDirection: sortDirection,
    };

    const filter = this.buildFilters();

    if (
      !this.creditApplicationReportExport.canStartTask(
        CREDIT_APPLICATION_EXPORT_ID
      )
    )
      return;

    const dialogRef = this.matDialog.open(ExportDialogComponent, {
      data: { title: CREDIT_APPLICATION_TITLE },
    });

    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.creditApplicationReportExport.export(
          options.format,
          options.range,
          this.totalItems,
          this.pageSize,
          this.pageIndex,
          CREDIT_APPLICATION_EXPORT_ID,
          CREDIT_APPLICATION_TITLE,
          CREDIT_APPLICATION_PROPERTIES,
          filter,
          optionsFilters
        );
      }
    });
  }

  getList(): void {
    if (this.isLoadingRequest) {
      return;
    }
    this.isLoadingRequest = true;
    this.isLoading = true;
    this.hasError = false;

    const sortDirection: "ASC" | "DESC" =
      this.sortDirection === "asc" ? "ASC" : "DESC";

    const options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.search,
      sort: this.getSortField(),
      sortDirection: sortDirection,
    };
    const filter = this.buildFilters();
    const hasRequestIds =
      this.requestIdsToFilter && this.requestIdsToFilter.length > 0;
    this.creditRequestService.findAllWithCount(options, filter).subscribe({
      next: (res: any) => {
        const responseData = res?.data?.data ?? [];
        const total = res?.data?.pageInfo?.[0]?.total ?? 0;

        this.data = responseData;
        this.totalItems = total;
        this.isLoading = false;
        this.isLoadingRequest = false;
        if (hasRequestIds) {
          this.requestIdsToFilter = [];
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.isLoadingRequest = false;
        this.hasError = true;
        this.errorMessage =
          err.error?.message || "Error al obtener las solicitudes de crédito";
        this.snackBar.open(this.errorMessage, "Cerrar", { duration: 5000 });
        if (hasRequestIds) {
          this.requestIdsToFilter = [];
        }
      },
    });
  }

  onFilter(): void {
    this.pageIndex = 1;
    this.getList();
  }

  onClearFilter(): void {
    this.form.reset();
    this.selectedStatuses = [];
    this.requestIdsToFilter = [];
    this.pageIndex = 1;
    this.getList();
  }

  // SmartGrid Event Handlers
  onSortChange(event: SortChangedEvent<CreditApplicationAttributes>): void {
    if (event.sortStates.length > 0) {
      const sortState = event.sortStates[0];
      this.sortActive = sortState.field as string;
      this.sortDirection = sortState.direction === 'asc' ? 'asc' : 'desc';
    } else {
      this.sortActive = 'creationDateTime';
      this.sortDirection = 'desc';
    }
    this.getList();
  }

  onPageChange(event: PageChangedEvent): void {
    this.pageIndex = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.getList();
  }

  onBusinessPartnersExpanded(): void {
    this.loadBusinessPartnersData = true;
  }

  /**
   * Helper: Format currency values for display and filtering
   */
  private formatCurrency(value: number): string {
    if (value == null) return '-';
    const decimals = this.decimalConfigService.getDecimals();
    const formattedNum = this.decimalPipe.transform(value, `1.${decimals}-${decimals}`) ?? '-';
    if (formattedNum === '-') return '-';
    const symbol = this.companyCurrencyPipe.transform('symbol');
    return `${symbol} ${formattedNum}`;
  }

  /**
   * Helper: Create filter options for currency columns
   */
  private createCurrencyFilterOptions(fieldPath: string) {
    return {
      valueExtractor: (item: any) => {
        const value = item[fieldPath];
        return this.formatCurrency(value);
      },
      customFilterFn: (rowData: any, filterValue: string) => {
        const value = rowData[fieldPath];
        const formattedValue = this.formatCurrency(value);
        if (!formattedValue || formattedValue === '-') return !filterValue;
        return formattedValue === filterValue;
      },
    };
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
